import log from "logbeacon";
import { getSupabaseBrowserClient } from "./supabaseBrowserClient";
import { shouldUseSupabaseNotificationSync } from "./notificationSyncBackend";
import { logNotificationSync } from "./notificationSyncTelemetry";

function canSyncToSupabase(): boolean {
  return typeof window !== "undefined" && shouldUseSupabaseNotificationSync();
}

export async function upsertLatestSeenToSupabase(
  ownerId: string,
  latestSeenId: string,
  latestSeenTime: number,
): Promise<void> {
  if (!canSyncToSupabase()) {
    return;
  }

  try {
    const supabase = getSupabaseBrowserClient();
    const { error } = await supabase.from("user_notification_prefs").upsert(
      {
        owner_id: ownerId,
        latest_seen_id: latestSeenId,
        latest_seen_time: latestSeenTime,
      },
      { onConflict: "owner_id" },
    );

    if (error) {
      log.warn("[notification][supabase] upsert latest seen failed", {
        ownerId,
        latestSeenId,
        error: error.message,
      });
      logNotificationSync("write_upsert", {
        ownerId,
        table: "user_notification_prefs",
        event: "upsert",
        status: "error",
        reason: error.message,
      });
      return;
    }
    logNotificationSync("write_upsert", {
      ownerId,
      table: "user_notification_prefs",
      event: "upsert",
      status: "ok",
      lagMs: Date.now() - latestSeenTime,
    });
  } catch (error) {
    log.warn("[notification][supabase] upsert latest seen error", {
      ownerId,
      latestSeenId,
      error: error instanceof Error ? error.message : String(error),
    });
    logNotificationSync("write_upsert", {
      ownerId,
      table: "user_notification_prefs",
      event: "upsert",
      status: "error",
      reason: error instanceof Error ? error.message : String(error),
    });
  }
}

export async function upsertNotifyMessageReadToSupabase(
  ownerId: string,
  messageId: string,
  readAt: number,
): Promise<void> {
  if (!canSyncToSupabase()) {
    return;
  }

  try {
    const supabase = getSupabaseBrowserClient();
    const { error } = await supabase
      .from("user_notification_message_reads")
      .upsert(
        {
          owner_id: ownerId,
          message_id: messageId,
          read_at: readAt,
        },
        { onConflict: "owner_id,message_id" },
      );

    if (error) {
      log.warn("[notification][supabase] upsert message read failed", {
        ownerId,
        messageId,
        error: error.message,
      });
      logNotificationSync("write_upsert", {
        ownerId,
        table: "user_notification_message_reads",
        event: "upsert",
        status: "error",
        reason: error.message,
      });
      return;
    }
    logNotificationSync("write_upsert", {
      ownerId,
      table: "user_notification_message_reads",
      event: "upsert",
      status: "ok",
      lagMs: Date.now() - readAt,
    });
  } catch (error) {
    log.warn("[notification][supabase] upsert message read error", {
      ownerId,
      messageId,
      error: error instanceof Error ? error.message : String(error),
    });
    logNotificationSync("write_upsert", {
      ownerId,
      table: "user_notification_message_reads",
      event: "upsert",
      status: "error",
      reason: error instanceof Error ? error.message : String(error),
    });
  }
}

export async function upsertNotifyActionHiddenToSupabase(
  ownerId: string,
  messageId: string,
  catId: string,
  hiddenAt: number,
): Promise<void> {
  if (!canSyncToSupabase()) {
    return;
  }

  try {
    const supabase = getSupabaseBrowserClient();
    const { error } = await supabase
      .from("user_notification_action_hides")
      .upsert(
        {
          owner_id: ownerId,
          message_id: messageId,
          cat_id: catId,
          hidden_at: hiddenAt,
        },
        { onConflict: "owner_id,message_id,cat_id" },
      );

    if (error) {
      log.warn("[notification][supabase] upsert action hidden failed", {
        ownerId,
        messageId,
        catId,
        error: error.message,
      });
      logNotificationSync("write_upsert", {
        ownerId,
        table: "user_notification_action_hides",
        event: "upsert",
        status: "error",
        reason: error.message,
      });
      return;
    }
    logNotificationSync("write_upsert", {
      ownerId,
      table: "user_notification_action_hides",
      event: "upsert",
      status: "ok",
      lagMs: Date.now() - hiddenAt,
    });
  } catch (error) {
    log.warn("[notification][supabase] upsert action hidden error", {
      ownerId,
      messageId,
      catId,
      error: error instanceof Error ? error.message : String(error),
    });
    logNotificationSync("write_upsert", {
      ownerId,
      table: "user_notification_action_hides",
      event: "upsert",
      status: "error",
      reason: error instanceof Error ? error.message : String(error),
    });
  }
}

export async function upsertTrackerReadToSupabase(
  ownerId: string,
  trackerId: string,
  readAt: number,
): Promise<void> {
  if (!canSyncToSupabase()) {
    return;
  }

  try {
    const supabase = getSupabaseBrowserClient();
    const { error } = await supabase.from("user_tracker_reads").upsert(
      {
        owner_id: ownerId,
        tracker_id: trackerId,
        read_at: readAt,
      },
      { onConflict: "owner_id,tracker_id" },
    );

    if (error) {
      log.warn("[tracker][supabase] upsert tracker read failed", {
        ownerId,
        trackerId,
        error: error.message,
      });
      logNotificationSync("write_upsert", {
        ownerId,
        table: "user_tracker_reads",
        event: "upsert",
        status: "error",
        reason: error.message,
      });
      return;
    }
    logNotificationSync("write_upsert", {
      ownerId,
      table: "user_tracker_reads",
      event: "upsert",
      status: "ok",
      lagMs: Date.now() - readAt,
    });
  } catch (error) {
    log.warn("[tracker][supabase] upsert tracker read error", {
      ownerId,
      trackerId,
      error: error instanceof Error ? error.message : String(error),
    });
    logNotificationSync("write_upsert", {
      ownerId,
      table: "user_tracker_reads",
      event: "upsert",
      status: "error",
      reason: error instanceof Error ? error.message : String(error),
    });
  }
}

const BRIEF_DAILY_QUOTA_TABLE = "user_brief_daily_quota_state";

export type BriefDailyQuotaSnapshot = {
  remainingQuota: number;
  quotaDateUtc: string;
  sourceUpdatedAt: number;
};

type BriefDailyQuotaRow = {
  remaining_quota: number;
  quota_date_utc: string;
  source_updated_at: number;
};

export async function fetchBriefDailyQuotaFromSupabase(
  ownerId: string,
): Promise<BriefDailyQuotaSnapshot | null> {
  if (!canSyncToSupabase()) {
    return null;
  }

  try {
    const supabase = getSupabaseBrowserClient();
    const { data, error } = await supabase
      .from(BRIEF_DAILY_QUOTA_TABLE)
      .select("remaining_quota,quota_date_utc,source_updated_at")
      .eq("owner_id", ownerId)
      .order("source_updated_at", { ascending: false })
      .limit(1)
      .returns<BriefDailyQuotaRow[]>();

    if (error) {
      log.warn("[brief][supabase] fetch daily quota failed", {
        ownerId,
        error: error.message,
      });
      return null;
    }

    const row = data?.[0];
    if (!row) {
      return null;
    }

    return {
      remainingQuota: row.remaining_quota,
      quotaDateUtc: row.quota_date_utc,
      sourceUpdatedAt: row.source_updated_at,
    };
  } catch (error) {
    log.warn("[brief][supabase] fetch daily quota error", {
      ownerId,
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

export async function upsertBriefDailyQuotaToSupabase(
  ownerId: string,
  snapshot: BriefDailyQuotaSnapshot,
): Promise<void> {
  if (!canSyncToSupabase()) {
    return;
  }

  try {
    const supabase = getSupabaseBrowserClient();
    const { data, error } = await supabase
      .from(BRIEF_DAILY_QUOTA_TABLE)
      .select("remaining_quota,source_updated_at")
      .eq("owner_id", ownerId)
      .eq("quota_date_utc", snapshot.quotaDateUtc)
      .maybeSingle<{ remaining_quota: number; source_updated_at: number }>();

    if (error) {
      log.warn("[brief][supabase] read daily quota before upsert failed", {
        ownerId,
        error: error.message,
      });
      return;
    }

    if (data && data.source_updated_at > snapshot.sourceUpdatedAt) {
      return;
    }

    const mergedQuota =
      data &&
      data.source_updated_at === snapshot.sourceUpdatedAt &&
      data.remaining_quota >= 0 &&
      snapshot.remainingQuota >= 0
        ? Math.min(data.remaining_quota, snapshot.remainingQuota)
        : snapshot.remainingQuota;

    const { error: upsertError } = await supabase
      .from(BRIEF_DAILY_QUOTA_TABLE)
      .upsert(
        {
          owner_id: ownerId,
          quota_date_utc: snapshot.quotaDateUtc,
          remaining_quota: mergedQuota,
          source_updated_at: snapshot.sourceUpdatedAt,
        },
        { onConflict: "owner_id,quota_date_utc" },
      );

    if (upsertError) {
      log.warn("[brief][supabase] upsert daily quota failed", {
        ownerId,
        error: upsertError.message,
      });
    }
  } catch (error) {
    log.warn("[brief][supabase] upsert daily quota error", {
      ownerId,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

export type BriefEndFeedbackSentiment = "" | "good" | "poor";

const BRIEF_END_FEEDBACK_TABLE = "user_notification_action_hides";

/** Brief EndCard 反馈复用 action_hides：message_id 与 owner_id 相同作哨兵，cat_id 存 sentiment */
function briefEndFeedbackMessageId(ownerId: string): string {
  return ownerId;
}

function parseBriefEndFeedbackSentiment(
  value: unknown,
): BriefEndFeedbackSentiment {
  if (value === "good" || value === "poor") {
    return value;
  }
  return "";
}

export async function fetchBriefEndFeedbackFromSupabase(
  ownerId: string,
): Promise<BriefEndFeedbackSentiment | null> {
  if (!canSyncToSupabase()) {
    return null;
  }

  try {
    const supabase = getSupabaseBrowserClient();
    const { data, error } = await supabase
      .from(BRIEF_END_FEEDBACK_TABLE)
      .select("cat_id")
      .eq("owner_id", ownerId)
      .eq("message_id", briefEndFeedbackMessageId(ownerId))
      .limit(1)
      .returns<{ cat_id: string }[]>();

    if (error) {
      log.warn("[brief][supabase] fetch end feedback failed", {
        ownerId,
        error: error.message,
      });
      return null;
    }

    const row = data?.[0];
    if (!row) {
      return null;
    }

    return parseBriefEndFeedbackSentiment(row.cat_id);
  } catch (error) {
    log.warn("[brief][supabase] fetch end feedback error", {
      ownerId,
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

export async function upsertBriefEndFeedbackToSupabase(
  ownerId: string,
  sentiment: BriefEndFeedbackSentiment,
): Promise<void> {
  if (!canSyncToSupabase()) {
    return;
  }

  const messageId = briefEndFeedbackMessageId(ownerId);

  try {
    const supabase = getSupabaseBrowserClient();
    const { error: deleteError } = await supabase
      .from(BRIEF_END_FEEDBACK_TABLE)
      .delete()
      .eq("owner_id", ownerId)
      .eq("message_id", messageId);

    if (deleteError) {
      log.warn("[brief][supabase] clear end feedback failed", {
        ownerId,
        sentiment,
        error: deleteError.message,
      });
      logNotificationSync("write_upsert", {
        ownerId,
        table: BRIEF_END_FEEDBACK_TABLE,
        event: "delete",
        status: "error",
        reason: deleteError.message,
      });
      return;
    }

    if (sentiment !== "good" && sentiment !== "poor") {
      logNotificationSync("write_upsert", {
        ownerId,
        table: BRIEF_END_FEEDBACK_TABLE,
        event: "delete",
        status: "ok",
      });
      return;
    }

    const hiddenAt = Date.now();
    const { error } = await supabase.from(BRIEF_END_FEEDBACK_TABLE).upsert(
      {
        owner_id: ownerId,
        message_id: messageId,
        cat_id: sentiment,
        hidden_at: hiddenAt,
      },
      { onConflict: "owner_id,message_id,cat_id" },
    );

    if (error) {
      log.warn("[brief][supabase] upsert end feedback failed", {
        ownerId,
        sentiment,
        error: error.message,
      });
      logNotificationSync("write_upsert", {
        ownerId,
        table: BRIEF_END_FEEDBACK_TABLE,
        event: "upsert",
        status: "error",
        reason: error.message,
      });
      return;
    }

    logNotificationSync("write_upsert", {
      ownerId,
      table: BRIEF_END_FEEDBACK_TABLE,
      event: "upsert",
      status: "ok",
      lagMs: Date.now() - hiddenAt,
    });
  } catch (error) {
    log.warn("[brief][supabase] upsert end feedback error", {
      ownerId,
      sentiment,
      error: error instanceof Error ? error.message : String(error),
    });
    logNotificationSync("write_upsert", {
      ownerId,
      table: BRIEF_END_FEEDBACK_TABLE,
      event: "upsert",
      status: "error",
      reason: error instanceof Error ? error.message : String(error),
    });
  }
}
